import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Logo } from '@/components/ui/Logo';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';

const PIN_LEN = 6;

export function LoginPage() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    if (state.status === 'authed') navigate('/', { replace: true });
  }, [state.status, navigate]);

  useEffect(() => {
    setError(null);
  }, [pin]);

  useEffect(() => {
    if (pin.length !== PIN_LEN || busy) return;
    let cancelled = false;
    (async () => {
      setBusy(true);
      try {
        await login(pin);
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof ApiError && err.status === 429
            ? 'Слишком много попыток. Попробуйте через минуту.'
            : err instanceof ApiError && err.status === 401
              ? 'Неверный PIN'
              : 'Не удалось войти';
        setError(msg);
        setShake(true);
        setTimeout(() => {
          setShake(false);
          setPin('');
        }, 380);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pin, busy, login]);

  const press = (digit: string) => {
    setPin((p) => (p.length >= PIN_LEN ? p : p + digit));
  };
  const back = () => setPin((p) => p.slice(0, -1));

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center px-6 py-10">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="flex flex-col items-center gap-5"
      >
        <Logo size={96} />
        <div className="text-center">
          <div className="text-[22px] font-semibold tracking-tight">Eye Laba</div>
          <div className="mt-1 text-sm text-ink-2">
            Личный AI на встречах. Вход по PIN.
          </div>
        </div>
      </motion.div>

      <motion.div
        animate={shake ? { x: [-8, 8, -6, 6, -3, 0] } : { x: 0 }}
        transition={{ duration: 0.38 }}
        className="mt-8 flex items-center gap-3"
      >
        {Array.from({ length: PIN_LEN }).map((_, i) => {
          const filled = i < pin.length;
          return (
            <span
              key={i}
              className={clsx(
                'block size-3 rounded-full transition-all',
                filled
                  ? 'bg-accent shadow-[0_0_12px_rgba(124,92,255,0.7)]'
                  : 'bg-white/15',
                error && filled && 'bg-danger shadow-[0_0_10px_rgba(255,107,107,0.6)]',
              )}
            />
          );
        })}
      </motion.div>

      <div className="mt-3 h-5 text-sm text-danger">{error ?? ' '}</div>

      <div className="mt-6 grid grid-cols-3 gap-3 w-full max-w-[320px]">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
          <KeyButton key={n} label={String(n)} onClick={() => press(String(n))} disabled={busy} />
        ))}
        <span />
        <KeyButton label="0" onClick={() => press('0')} disabled={busy} />
        <KeyButton
          label="⌫"
          onClick={back}
          disabled={busy || pin.length === 0}
          variant="ghost"
        />
      </div>

      <div className="mt-10 text-xs text-ink-3">
        {busy ? 'Проверяю…' : 'Введите 6 цифр'}
      </div>
    </div>
  );
}

function KeyButton({
  label,
  onClick,
  disabled,
  variant = 'solid',
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'solid' | 'ghost';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={clsx(
        'h-16 rounded-2xl text-2xl font-medium transition active:scale-95 focus-ring',
        variant === 'solid'
          ? 'glass-strong hover:bg-white/[0.1]'
          : 'text-ink-2 hover:bg-white/[0.06]',
        'disabled:opacity-40 disabled:active:scale-100',
      )}
    >
      {label}
    </button>
  );
}
